import type { ReactNode } from "react"

export type GroupLabel = {
  key: string
  label: string
  detail?: string
  order?: number
  icon?: ReactNode
  total?: number
}

export type Group<R> = GroupLabel & {
  rows: R[]
}

export type GroupsOf<R> = (row: R) => GroupLabel | GroupLabel[]

export function groupInOrder<R>(rows: R[], groupOf: GroupsOf<R>): Group<R>[] {
  const groups = new Map<string, Group<R>>()
  for (const row of rows) {
    for (const label of [groupOf(row)].flat()) {
      const group = groups.get(label.key) ?? { ...label, rows: [] }
      group.rows.push(row)
      groups.set(label.key, group)
    }
  }
  return [...groups.values()]
}

export function byGroupOrder(a: GroupLabel, b: GroupLabel): number {
  return (a.order ?? 0) - (b.order ?? 0) || a.label.localeCompare(b.label)
}

export function groupRows<R>(rows: R[], groupOf: GroupsOf<R>): Group<R>[] {
  return groupInOrder(rows, groupOf).sort(byGroupOrder)
}

export function withEmptyGroups<R>(
  present: Group<R>[],
  empty: GroupLabel[],
): Group<R>[] {
  const keys = new Set(present.map((group) => group.key))
  const missing = empty
    .filter((label) => !keys.has(label.key))
    .map((label) => ({ ...label, rows: [] }))
  return missing.length > 0
    ? [...present, ...missing].sort(byGroupOrder)
    : present
}
